<template>
  <div class="app-notifications__item">
    <div
      class="app-notifications__header"
      :class="{ 'app-notifications__header--expandable': isExpandable }"
      @click="toggleExpanded"
    >
      <p class="app-notifications__date">
        {{ displayDate }}
      </p>
      <div class="app-notifications__status">
        {{ item.status }}
      </div>
      <div v-if="$slots.action" class="app-notifications__action">
        <slot name="action" />
      </div>
    </div>
    <div class="app-notifications__content">
      <div
        ref="messageRef"
        v-tooltip="item.message"
        class="app-notifications__message"
        :class="{ 'app-notifications__message--expanded': isExpanded }"
        @click="toggleExpanded"
      >
        {{ item.message }}
      </div>
    </div>
    <div v-if="isExpanded && item.error" class="app-notifications__error">
      {{ item.error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { TaskNotify } from './utils/types'

import { EMPTY_VALUE_PLACEHOLDER } from '@/const'

import { stringToLocalDateString } from '@/helpers/dates'

type Props = {
  item: TaskNotify
}

const { item } = defineProps<Props>()

const isExpanded = ref(false)
const messageRef = ref<HTMLElement>()

const isExpandable = computed(() => !!item.error)

const displayDate = computed(() => {
  if (!item.date && !item.datetime) return EMPTY_VALUE_PLACEHOLDER
  return stringToLocalDateString(item.date || item.datetime, { withTime: !item.date })
})

const toggleExpanded = () => {
  if (!isExpandable.value) return

  isExpanded.value = !isExpanded.value
}
</script>

<style>
.app-notifications {
  &__item {
    @apply relative;
    @apply p-4;
  }

  &__header {
    @apply flex items-center justify-between;

    &--expandable {
      @apply cursor-pointer;
    }
  }

  &__date {
    @apply mr-2;
    @apply truncate text-xs font-medium text-indigo-600;
  }

  &__content {
    @apply sm:flex sm:items-start sm:justify-between;
    @apply sm:gap-2;
  }

  &__message {
    @apply sm:max-w-sm pt-0.5;
    @apply sm:flex;
    @apply text-base sm:text-sm text-gray-500;
    @apply line-clamp-2;

    &--expanded {
      @apply line-clamp-none;
    }
  }

  &__status {
    @apply py-1 ml-auto;
    @apply text-xs text-gray-400 whitespace-nowrap;
  }

  &__error {
    @apply p-3 mt-2;
    @apply bg-red-50 dark:bg-red-900/20;
    @apply border-l-2 border-red-400;
    @apply text-sm text-red-700 dark:text-red-300;
    @apply font-mono;
    @apply rounded-r;
  }
}
</style>
